import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

const navLinks = [
  { path: '/',         label: 'Home' },
  { path: '/about',    label: 'About' },
  { path: '/services', label: 'Services' },
  { path: '/contact',  label: 'Contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location  = useLocation();
  const isHome    = location.pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onScroll = () => { if (menuOpen) setMenuOpen(false); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [menuOpen]);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const isScrolledOrNotHome = scrolled || !isHome;

  const navStyle = {
    background:           'rgba(255,255,255,0.95)',
    backdropFilter:       'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    boxShadow:            isScrolledOrNotHome
      ? '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05)'
      : '0 4px 24px rgba(0,0,0,0.08)',
    borderColor:          'rgba(229,231,235,0.7)',
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b"
      style={navStyle}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 flex items-center justify-between h-[60px]">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <img
            src="/images/logo.jpg"
            alt="Lidav Enterprises Logo"
            className="h-9 w-9 rounded-full object-contain ring-2 ring-black/10 group-hover:ring-brand-red transition-all duration-300 shadow-sm"
          />
          <div className="leading-tight">
            <div className="flex items-baseline gap-0.5">
              <span className="text-gray-900 font-bold text-[14px] font-poppins tracking-wide">LIDAV</span>
              <span className="text-brand-red font-bold text-[14px] font-poppins"> ENTERPRISES</span>
            </div>
            <p className="text-gray-500 text-[9px] tracking-widest uppercase font-inter">Limited</p>
          </div>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-0.5">
          {navLinks.map(({ path, label }) => (
            <Link
              key={path}
              to={path}
              className={`relative px-3.5 py-2 text-[13px] font-medium font-inter transition-all duration-200 rounded-lg group
                ${isActive(path)
                  ? 'text-brand-red'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-black/5 cursor-pointer'}`}
            >
              {label}
              <span
                className={`absolute bottom-1 left-3.5 right-3.5 h-0.5 rounded-full bg-brand-red transition-all duration-300
                  ${isActive(path)
                    ? 'opacity-100 scale-x-100'
                    : 'opacity-0 scale-x-0 group-hover:opacity-100 group-hover:scale-x-100'}`}
              />
            </Link>
          ))}

          <div className="ml-3 flex items-center gap-2.5">
            <a
              href="tel:0722148999"
              className="hidden xl:flex items-center gap-1.5 text-gray-600 hover:text-gray-900 text-[12px] transition-colors font-inter cursor-pointer"
            >
              <Phone size={11} />
              <span>0722 148 999</span>
            </a>
            <Link
              to="/contact"
              className="bg-brand-red hover:bg-brand-red-dark text-white px-5 py-2 rounded-full text-[12px] font-semibold font-poppins transition-all duration-200 shadow-md hover:shadow-glow-red hover:-translate-y-0.5 cursor-pointer"
            >
              Free Quote
            </Link>
          </div>
        </nav>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-gray-700 p-2 rounded-xl hover:bg-black/5 transition-colors cursor-pointer"
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle menu"
        >
          <AnimatePresence mode="wait">
            {menuOpen
              ? <motion.div key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.18 }}><X size={20} /></motion.div>
              : <motion.div key="m" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.18 }}><Menu size={20} /></motion.div>
            }
          </AnimatePresence>
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            className="lg:hidden overflow-hidden border-t"
            style={{
              borderColor:          'rgba(0,0,0,0.06)',
              background:           'rgba(255,255,255,0.97)',
              backdropFilter:       'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {navLinks.map(({ path, label }, i) => (
                <motion.div
                  key={path}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={path}
                    className={`block py-2.5 px-4 rounded-xl text-sm font-medium font-inter transition-colors cursor-pointer
                      ${isActive(path)
                        ? 'bg-brand-blue/8 text-brand-red font-semibold'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'}`}
                  >
                    {label}
                  </Link>
                </motion.div>
              ))}
              <div className="pt-3 border-t border-gray-100 flex flex-col gap-2">
                <a href="tel:0722148999" className="flex items-center gap-2 text-gray-600 text-sm py-2 px-4 font-inter cursor-pointer">
                  <Phone size={13} /> 0722 148 999
                </a>
                <Link
                  to="/contact"
                  className="bg-brand-red text-white text-center py-3 px-4 rounded-full text-sm font-semibold font-poppins hover:bg-brand-red-dark transition-colors cursor-pointer"
                >
                  Get a Free Quote
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
